import { BlueprintRenderer, usePages } from '@cobliteam/blueprint-react-renderer';
import { useMarkNotificationAsRead } from '@cobliteam/hooks/src/useEntity/useAlerts';
import Loading from '@cobliteam/ui/components/Loading';
import { endOfDay, startOfDay } from '@cobliteam/utils/date';
import { useEffect, useRef, useState } from 'react';

const today = new Date();
const limitDay = new Date(new Date().setDate(today.getDate() - 6));
const startOfDayISO = startOfDay(limitDay).toISOString();
const endOfDayISO = endOfDay(today).toISOString();

type NotificationBellParams = {
  startTime: string;
  endTime: string;
  page?: number;
  size?: number;
};

type NotificationItem = { id: string; timestamp: string; isRead: boolean };

const MarkAsReadOnMount = ({ id, createdAt, onError }: { id: number; createdAt: string; onError: () => void }) => {
  const { mutate } = useMarkNotificationAsRead(id, createdAt);
  useEffect(() => {
    mutate(undefined, { onError });
  }, [mutate]);
  return null;
};

const BlueprintNotificationsList = () => {
  const [variables, setVariables] = useState<NotificationBellParams>({
    startTime: startOfDayISO,
    endTime: endOfDayISO,
    page: 0,
  });

  const { data, isFetching, wrap } = usePages({
    page: 'NotificationBell',
    variables: [variables, setVariables],
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const accumulatedRef = useRef<NotificationItem[]>([]);
  const prevPageRef = useRef<number>(-1);
  const markedAsReadRef = useRef<Set<string>>(new Set());
  const rollbackRef = useRef<Map<string, () => void>>(new Map());
  const [pendingItem, setPendingItem] = useState<{ id: number; createdAt: string } | null>(null);

  useEffect(() => {
    if (!(containerRef.current && sentinelRef.current) || isFetching) return;

    const pagination = (data as any)?.data?.presentation?.content?.pagination;
    if (!pagination || pagination.page >= pagination.totalPages - 1) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVariables((v) => ({ ...v, page: pagination.page + 1 }));
      },
      { root: containerRef.current, threshold: 0 }
    );

    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [data, isFetching]);

  useEffect(() => {
    const items: any[] = (data as any)?.data?.presentation?.content?.items ?? [];
    const page: number = (data as any)?.data?.presentation?.content?.pagination?.page ?? 0;
    if (!items.length) return;

    const mapped = items.map((item: any) => ({ id: item.id, timestamp: item.timestamp, isRead: item.isRead }));

    if (page > prevPageRef.current) {
      accumulatedRef.current = [...accumulatedRef.current, ...mapped];
    } else {
      accumulatedRef.current = mapped;
      markedAsReadRef.current = new Set();
    }
    prevPageRef.current = page;
  }, [data]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseEnter = (e: MouseEvent) => {
      const notifEl = (e.target as HTMLElement).closest('[data-testid="notification-item"]');
      if (!notifEl) return;

      const all = container.querySelectorAll('[data-testid="notification-item"]');
      const index = Array.from(all).indexOf(notifEl as HTMLElement);
      const item = accumulatedRef.current[index];

      if (!item || item.isRead || markedAsReadRef.current.has(item.id)) return;

      markedAsReadRef.current.add(item.id);
      (notifEl as HTMLElement).classList.remove('bg-primary-2');
      (notifEl as HTMLElement).classList.add('bg-white');
      rollbackRef.current.set(item.id, () => {
        markedAsReadRef.current.delete(item.id);
        (notifEl as HTMLElement).classList.remove('bg-white');
        (notifEl as HTMLElement).classList.add('bg-primary-2');
      });
      setPendingItem({ id: Number.parseInt(item.id), createdAt: item.timestamp });
    };

    container.addEventListener('mouseover', handleMouseEnter);
    return () => container.removeEventListener('mouseover', handleMouseEnter);
  }, []);

  return wrap(
    <div ref={containerRef} className='max-h-[50vh] overflow-y-auto'>
      <BlueprintRenderer isLoading={isFetching && !data} response={data} />
      {isFetching && data && (
        <div className='flex justify-center py-3'>
          <Loading className='size-6' />
        </div>
      )}
      <div ref={sentinelRef} style={{ height: '1px' }} />
      {pendingItem && (
        <MarkAsReadOnMount
          key={pendingItem.id}
          id={pendingItem.id}
          createdAt={pendingItem.createdAt}
          onError={() => {
            rollbackRef.current.get(String(pendingItem.id))?.();
            rollbackRef.current.delete(String(pendingItem.id));
          }}
        />
      )}
    </div>
  );
};

export default BlueprintNotificationsList;
